/**
 * Platform (Super Admin) auth context — completely separate from product AuthContext.
 * Uses storage keys: platform_access_token, platform_refresh_token (no mixing with product).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { platformAuthApi } from '../platformApi/auth';
import {
  registerPlatformHttpCallbacks,
  setPlatformAccessToken,
} from '../platformApi/http';
import { SecureStorage } from '../platform/secureStorage';

const PLATFORM_ACCESS_TOKEN_KEY = 'platform_access_token';
const PLATFORM_REFRESH_TOKEN_KEY = 'platform_refresh_token';

const EXP_BUFFER_MS = 10_000;

interface PlatformAuthState {
  isLoggedIn: boolean;
  isLoading: boolean;
}

interface PlatformAuthContextValue extends PlatformAuthState {
  login: (email: string, password: string) => Promise<void>;
  /** Imposta sessione platform da token (es. dopo login unificato che ha riconosciuto SUPER_ADMIN). */
  setSessionFromTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshOnce: () => Promise<string>;
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null);

function isPlatformTokenValid(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp as number | undefined;
    if (typeof exp !== 'number') return false;
    return exp * 1000 - EXP_BUFFER_MS > Date.now();
  } catch {
    return false;
  }
}

export function PlatformAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PlatformAuthState>({
    isLoggedIn: false,
    isLoading: true,
  });
  const logoutRef = useRef<() => Promise<void>>(async () => {});

  const logout = useCallback(async () => {
    const refreshToken = await SecureStorage.get(PLATFORM_REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await platformAuthApi.logout({ refreshToken });
      } catch {
        // best-effort
      }
    }
    setPlatformAccessToken(null);
    await SecureStorage.remove(PLATFORM_ACCESS_TOKEN_KEY);
    await SecureStorage.remove(PLATFORM_REFRESH_TOKEN_KEY);
    setState({ isLoggedIn: false, isLoading: false });
  }, []);

  logoutRef.current = logout;

  const refreshOnce = useCallback(async (): Promise<string> => {
    const refreshToken = await SecureStorage.get(PLATFORM_REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('No platform refresh token');

    const res = await platformAuthApi.refresh({ refreshToken });
    setPlatformAccessToken(res.accessToken);
    await SecureStorage.set(PLATFORM_ACCESS_TOKEN_KEY, res.accessToken);
    await SecureStorage.set(PLATFORM_REFRESH_TOKEN_KEY, res.refreshToken);
    return res.accessToken;
  }, []);

  useEffect(() => {
    registerPlatformHttpCallbacks(refreshOnce, () => {
      logoutRef.current();
    });
  }, [refreshOnce]);

  useEffect(() => {
    (async () => {
      const accessToken = await SecureStorage.get(PLATFORM_ACCESS_TOKEN_KEY);
      if (accessToken && isPlatformTokenValid(accessToken)) {
        setPlatformAccessToken(accessToken);
        setState({ isLoggedIn: true, isLoading: false });
        return;
      }

      const refreshToken = await SecureStorage.get(PLATFORM_REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        setState({ isLoggedIn: false, isLoading: false });
        return;
      }
      try {
        const res = await platformAuthApi.refresh({ refreshToken });
        setPlatformAccessToken(res.accessToken);
        await SecureStorage.set(PLATFORM_ACCESS_TOKEN_KEY, res.accessToken);
        await SecureStorage.set(PLATFORM_REFRESH_TOKEN_KEY, res.refreshToken);
        setState({ isLoggedIn: true, isLoading: false });
      } catch {
        await SecureStorage.remove(PLATFORM_ACCESS_TOKEN_KEY);
        await SecureStorage.remove(PLATFORM_REFRESH_TOKEN_KEY);
        setState({ isLoggedIn: false, isLoading: false });
      }
    })();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await platformAuthApi.login({ email, password });
    setPlatformAccessToken(res.accessToken);
    await SecureStorage.set(PLATFORM_ACCESS_TOKEN_KEY, res.accessToken);
    await SecureStorage.set(PLATFORM_REFRESH_TOKEN_KEY, res.refreshToken);
    setState({ isLoggedIn: true, isLoading: false });
  }, []);

  const setSessionFromTokens = useCallback(async (accessToken: string, refreshToken: string) => {
    setPlatformAccessToken(accessToken);
    await SecureStorage.set(PLATFORM_ACCESS_TOKEN_KEY, accessToken);
    await SecureStorage.set(PLATFORM_REFRESH_TOKEN_KEY, refreshToken);
    setState({ isLoggedIn: true, isLoading: false });
  }, []);

  return (
    <PlatformAuthContext.Provider
      value={{ ...state, login, setSessionFromTokens, logout, refreshOnce }}
    >
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth(): PlatformAuthContextValue {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be inside PlatformAuthProvider');
  return ctx;
}
